import { useCallback } from 'react';
import type { MemberPin, PinKind } from '@/lib/api/types';
import { usePin, usePins, useUnpin } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { findPin } from './pinsModel';

/** Pin state + toggle for one target, for buttons in headers, menus and overlays (S1). */
export function usePinTarget(kind: PinKind, targetId: string | null | undefined) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const isMember = useSessionStore((s) => s.principal.kind === 'member');
  const pins = usePins(isMember ? wsId : undefined);
  const pin = usePin(wsId);
  const unpin = usePinUnpin(wsId);
  const current: MemberPin | undefined = targetId ? findPin(pins.data, kind, targetId) : undefined;
  const toggle = useCallback(() => {
    if (!targetId) return;
    if (current) unpin.mutate({ kind, targetId });
    else pin.mutate({ kind, targetId }, { onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not pin' }) });
  }, [current, kind, targetId, pin, unpin]);
  return { pinned: !!current, toggle, available: isMember, busy: pin.isPending || unpin.isPending };
}

function usePinUnpin(wsId: string) {
  return useUnpin(wsId);
}
